import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/app/components/ui/sheet";

import Loader from "../../../components/Loader";
import FeedbackUntyped from "../../../components/Reviews/FeedBack";
import sectionNamesFromRoutes from "../../../seed/sectionNamesFromRoutes";

// Untyped JSX component; `sectionNavigate` is unused when navigateToSections is false.
const Feedback = FeedbackUntyped as unknown as React.ComponentType<{
	feedback: ResumeFeedbackItem;
	navigateToSections: boolean;
}>;

export interface ResumeFeedbackItem {
	_id: string;
	section: string;
	[key: string]: unknown;
}

interface ResumeFeedbackModalProps {
	feedbacks: ResumeFeedbackItem[];
	isFeedbackOpen: boolean;
	toggleFeedbackModal: () => void;
	feedbackLoading: boolean;
}

function ResumeFeedbackModal({
	feedbacks,
	isFeedbackOpen,
	toggleFeedbackModal,
	feedbackLoading,
}: ResumeFeedbackModalProps) {
	const location = useLocation();
	const [sectionName, setSectionName] = useState("");
	const [filteredFeedbacks, setFilteredFeedbacks] = useState<ResumeFeedbackItem[]>([]);

	const filterFeedbacks = useCallback(() => {
		if (feedbacks.length > 0) {
			const _sectionName = sectionNamesFromRoutes(location.pathname);
			const _filteredFeedbacks = feedbacks.filter((feedback) => {
				return _sectionName === feedback.section;
			});

			setSectionName(_sectionName);
			setFilteredFeedbacks(_filteredFeedbacks);
		}
	}, [feedbacks, location.pathname]);

	useEffect(() => {
		filterFeedbacks();
	}, [filterFeedbacks]);

	return (
		<Sheet
			open={isFeedbackOpen}
			onOpenChange={(open) => {
				if (open !== isFeedbackOpen) toggleFeedbackModal();
			}}
		>
			<SheetContent side="right" className="w-full gap-0 sm:max-w-[521px]">
				<SheetHeader className="border-b">
					<SheetTitle className="pr-8">
						{feedbackLoading
							? "Loading feedbacks ...."
							: filteredFeedbacks.length === 0 && isFeedbackOpen
								? "Resume Feedbacks."
								: `Resume Feedbacks for ${sectionName.toLowerCase()} section`}
					</SheetTitle>
				</SheetHeader>
				{feedbackLoading ? (
					<div className="flex min-h-[80vh] items-center justify-center p-4">
						<Loader />
					</div>
				) : filteredFeedbacks.length === 0 && isFeedbackOpen ? (
					<div className="min-h-[80vh] p-6">
						<h6 className="font-semibold">No Feedback Received for this section</h6>
					</div>
				) : (
					<div className="flex-1 overflow-y-auto p-4 pb-10">
						{filteredFeedbacks.map((feedback) => (
							<Feedback key={feedback._id} feedback={feedback} navigateToSections={false} />
						))}
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}

export default ResumeFeedbackModal;
